import { CognitiveRouteRecord } from '@/router/routes/types'

const routes = [
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/pages/Admin.vue'),
    children: [
      {
        name: 'Batches',
        path: 'batches',
        redirect: '/admin/batches/list',
        component: () => import('@/pages/PassThrough.vue'),
        meta: {
          title: 'Batches',
          requiredRoles: ['user', 'admin'],
        },
        children: [
          {
            path: 'list',
            name: 'BatchList',
            redirect: '/admin/batches/list/pipeline',
            component: () => import('@/pages/admin/batches/list/Index.vue'),
            meta: {
              title: 'Batch Lists',
              requiredRoles: ['user', 'admin'],
            },
            children: [
              {
                path: 'pipeline',
                name: 'PipelineList',
                component: () => import('@/pages/admin/batches/list/Pipeline.vue'),
                meta: {
                  title: 'Pipeline Batch List',
                  description: 'Pipeline runs',
                  requiredRoles: ['user', 'admin'],
                },
              },
              {
                path: 'market-data',
                name: 'MarketDataList',
                component: () => import('@/pages/admin/batches/list/MarketData.vue'),
                meta: {
                  title: 'Market Data Batch List',
                  description: 'Market data runs',
                  requiredRoles: ['user', 'admin'],
                },
              },
              {
                path: 'on-demand',
                name: 'OnDemandList',
                component: () => import('@/pages/admin/batches/list/OnDemand.vue'),
                meta: {
                  title: 'On-Demand Batch List',
                  description: 'On-demand runs',
                  requiredRoles: ['user', 'admin'],
                },
              },
            ],
          },
          {
            path: 'diff',
            name: 'BatchDiff',
            redirect: '/admin/batches/diff/pipeline',
            component: () => import('@/pages/admin/batches/diff/Index.vue'),
            meta: {
              title: 'Batch Diffs',
              requiredRoles: ['user', 'admin'],
            },
            children: [
              {
                path: 'pipeline',
                name: 'PipelineDiff',
                component: () => import('@/pages/admin/batches/diff/Pipeline.vue'),
                meta: {
                  title: 'Pipeline Batch Diff',
                  description: 'Pipeline runs',
                  requiredRoles: ['user', 'admin'],
                },
                children: [
                  {
                    path: ':batchA([-A-Za-z]+)...:batchB([-A-Za-z]+):ignoreMe(.*)', // Backwards compatibility
                    redirect: (to) => ({
                      name: 'CompaniesDiff',
                      params: {
                        batchA: to.params.batchA,
                        batchB: to.params.batchB,
                      },
                      hash: `#${to.query.company_id || to.query.companyId || ''}`,
                      query: {},
                      path: `${to.params.batchB}/${to.params.batchA}/companies`,
                    }),
                  },
                  {
                    path: ':batchA/:batchB',
                    name: 'PipelineDiffList',
                    component: () => import('@/components/admin/batches/diff/PipelineDiffList.vue'),
                    redirect: (to) => ({
                      name: 'CompaniesDiff',
                      params: {
                        batchA: to.params.batchA,
                        batchB: to.params.batchB,
                      },
                      query: {},
                    }),
                    meta: {
                      title: 'Pipeline Batch Diff',
                      description: 'Pipeline runs',
                      requiredRoles: ['user', 'admin'],
                    },
                    children: [
                      {
                        path: 'documents',
                        name: 'DocumentsDiff',
                        component: () => import('@/components/admin/batches/diff/PipelineDiffDocuments.vue'),
                        meta: {
                          title: 'Documents',
                          requiredRoles: ['user', 'admin'],
                        },
                      },
                      {
                        path: 'companies',
                        name: 'CompaniesDiff',
                        component: () => import('@/components/admin/batches/diff/PipelineDiffCompanies.vue'),
                        props: true,
                        meta: {
                          title: 'Companies',
                          requiredRoles: ['user', 'admin'],
                        },
                      },
                    ],
                  },
                ],
              },
              {
                path: 'market-data',
                name: 'MarketDataDiff',
                component: () => import('@/pages/admin/batches/diff/MarketData.vue'),
                meta: {
                  title: 'Market Data Batch Diff',
                  description: 'Market data runs',
                  requiredRoles: ['user', 'admin'],
                },
              },
            ],
          },
          {
            path: 'details/:batch',
            redirect: (to) => ({ name: 'Batch Companies', params: { batch: to.params.batch } }),
            component: () => import('@/pages/admin/batches/_batch.vue'),
            props: true,
            meta: {
              title: 'Batch detail',
              requiredRoles: ['user', 'admin'],
            },
            children: [
              {
                name: 'Batch Companies',
                path: 'companies',
                component: () => import('@/components/admin/batches/list/CompanyDetail.vue'),
                meta: {
                  title: 'Batch Companies',
                  requiredRoles: ['user', 'admin'],
                },
              },
              {
                name: 'Batch Documents',
                path: 'documents',
                component: () => import('@/components/admin/batches/list/DocumentDetail.vue'),
                props: {
                  default: true,
                },
                meta: {
                  title: 'Batch Documents',
                  requiredRoles: ['user', 'admin'],
                },
              },
            ],
          },
        ],
      },
      {
        name: 'Documents',
        path: 'documents',
        component: () => import('@/pages/admin/Documents.vue'),
        meta: {
          title: 'Documents',
          requiredRoles: ['user', 'admin'],
        },
      },
      {
        name: 'DocumentTools',
        path: 'documents/tools',
        component: () => import('@/pages/admin/ToolsPage.vue'),
        redirect: { name: 'DocumentWatermarkRemoval' },
        children: [
          {
            name: 'DocumentWatermarkRemoval',
            path: 'watermark',
            component: () => import('@/pages/admin/documentTools/WatermarkRemoval.vue'),
            meta: {
              title: 'Document Watermark Removal',
              requiredRoles: ['user', 'admin'],
            },
          },
          {
            name: 'DocumentWatermarkHistory',
            path: 'watermarkhistory',
            component: () => import('@/pages/admin/documentTools/WatermarkRemovalHistory.vue'),
            meta: {
              title: 'Document Watermark History',
              requiredRoles: ['user', 'admin'],
            },
          },
          {
            name: 'DocumentPassword',
            path: 'password',
            component: () => import('@/pages/admin/documentTools/PasswordRemovalPage.vue'),
            meta: {
              title: 'Document Password Removal',
              requiredRoles: ['user', 'admin'],
            },
          },
          {
            name: 'DocumentSplit',
            path: 'split',
            component: () => import('@/pages/admin/documentTools/SplitPage.vue'),
            meta: {
              title: 'Document Split',
              requiredRoles: ['user', 'admin'],
            },
          },
        ],
        meta: {
          title: 'Document Tools',
          requiredRoles: ['user', 'admin'],
        },
      },
      {
        name: 'DocEditor',
        path: 'documents/:document',
        component: () => import('@/pages/admin/DocumentEditorPage.vue'),
        meta: {
          title: 'Document Editor',
          requiredRoles: ['user', 'admin'],
        },
      },
      {
        name: 'AdminCompanies',
        path: 'companies',
        component: () => import('@/pages/admin/CompaniesPage.vue'),
        meta: {
          title: 'Companies',
          requiredRoles: ['user', 'admin'],
        },
      },
      {
        name: 'CompanyDocumentList',
        path: 'companies/:company/documents',
        component: () => import('@/pages/admin/CompanyDocumentListPage.vue'),
        meta: {
          title: 'Company Documents',
          requiredRoles: ['user', 'admin'],
          legacyBailoutPath: (params) => `/admin/documents#${params.company}`,
        },
      },
      {
        name: 'CompanyDocuments',
        path: 'companies/:company/documents/upload',
        component: () => import('@/pages/admin/CompanyDocumentsPage.vue'),
        meta: {
          title: 'Company Documents Upload',
          requiredRoles: ['user', 'admin'],
        },
      },
      {
        name: 'Company Edit',
        path: 'companies/:company',
        redirect: (to) => ({ name: 'Edit', params: { company: to.params.company } }),
        component: () => import('@/pages/admin/ModalPage.vue'),
        meta: {
          title: 'Company',
          requiredRoles: ['user', 'admin'],
        },
        children: [
          {
            name: 'Edit',
            path: 'edit/:tab?',
            component: () => import('@/components/admin/companies/CompanyEdit/CompanyEdit.vue'),
            meta: {
              title: 'Company Edit',
              requiredRoles: ['user', 'admin'],
              default: true,
            },
          },
          {
            name: 'DataChecks',
            path: 'data-checks',
            component: () => import('@/pages/PassThrough.vue'),
            meta: {
              title: 'Company Data Checks',
              requiredRoles: ['user', 'admin'],
            },
            children: [
              {
                alias: '',
                path: 'view',
                name: 'Data Checks Company Modal View',
                component: () => import('@/components/admin/dataChecks/ViewDataCheck/ViewDataCheck.vue'),
              },
            ],
          },
        ],
      },
      {
        name: 'Queries',
        path: 'queries',
        component: () => import('@/pages/admin/UserQueries.vue'),
        redirect: '/admin/queries/data',
        meta: {
          title: 'User Queries',
          requiredRoles: ['user'],
          optionalRoles: ['admin', 'dataAnalyst'],
        },
        children: [
          {
            path: 'data',
            name: 'Data queries',
            component: () => import('@/pages/admin/queries/DataQueries.vue'),
            meta: {
              title: 'Data queries',
              requiredRoles: ['user'],
              optionalRoles: ['admin', 'dataAnalyst'],
            },
          },
          {
            path: 'coverage',
            name: 'Coverage requests',
            component: () => import('@/pages/admin/queries/CoverageRequests.vue'),
            meta: {
              title: 'Coverage requests',
              requiredRoles: ['user'],
              optionalRoles: ['admin', 'dataAnalyst'],
            },
          },
          {
            path: 'documents',
            name: 'Document uploads',
            component: () => import('@/pages/admin/queries/DocumentUploads.vue'),
            meta: {
              title: 'Document uploads',
              requiredRoles: ['user'],
              optionalRoles: ['admin', 'dataAnalyst'],
            },
          },
        ],
      },
      {
        name: 'Data Checks',
        path: 'data-checks/',
        component: () => import('@/pages/admin/DataChecks.vue'),
        meta: {
          title: 'Data Checks',
          requiredRoles: ['user', 'admin'],
        },
        children: [
          {
            name: 'Run Data Check',
            path: 'run/:company?',
            component: () => import('@/pages/admin/ModalPage.vue'),
            meta: {
              title: 'Run Data Check',
              requiredRoles: ['user', 'admin'],
            },
            children: [
              {
                alias: '',
                path: 'run',
                component: () => import('@/components/admin/dataChecks/RunDataCheck/RunDataCheck.vue'),
                children: [
                  {
                    name: 'Earnings Summary Email Preview',
                    path: 'earnings-summary',
                    component: () =>
                      import(
                        '@/components/admin/dataChecks/EarningsSummaryEmailPreview/EarningsSummaryEmailPreview.vue'
                      ),
                  },
                ],
              },
            ],
          },
          {
            name: 'Data Checks Detail',
            path: ':batch/details/',
            component: () => import('@/pages/admin/DataChecksDetail.vue'),
            meta: {
              title: 'Data Checks Detail',
              requiredRoles: ['user', 'admin'],
            },
            children: [
              {
                name: 'Data Checks Detail Modal',
                path: ':company',
                component: () => import('@/pages/admin/ModalPage.vue'),
                meta: {
                  title: 'Data Checks Detail Modal',
                  requiredRoles: ['user', 'admin'],
                },
                children: [
                  {
                    alias: '',
                    path: 'view',
                    name: 'Data Checks Detail Modal View',
                    component: () => import('@/components/admin/dataChecks/ViewDataCheck/ViewDataCheck.vue'),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
    meta: {
      auth: true,
      requiredRoles: ['user'],
      optionalRoles: ['admin', 'dataAnalyst'],
      title: 'Admin',
    },
  },
  {
    // The page for viewing the output file of a batch is deliberately
    // not nested under the admin path because we don't want the
    // navigation components as part of the layout.
    name: 'Batch Output File',
    path: '/admin/batches/file/:filePath(.*)',
    component: () => import('@/pages/admin/batches/BatchOutputFile.vue'),
  },
] as const satisfies CognitiveRouteRecord[]

export default routes
